(() => {
  'use strict';

  const DEBOUNCE_MS = 140;

  const getShopifyRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const getChangeUrl = () => `${getShopifyRoot()}cart/change.js`;

  const parseSection = (html, selector) => {
    if (!html || typeof html !== 'string') return null;
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    return template.content.querySelector(selector);
  };

  const updateHeaderCount = (count) => {
    const safeCount = Math.max(0, Number(count) || 0);

    document.querySelectorAll('.wi-header__cart').forEach((cartLink) => {
      cartLink.setAttribute(
        'aria-label',
        `Cart, ${safeCount} ${safeCount === 1 ? 'item' : 'items'}`
      );
      cartLink.dataset.cartCount = String(safeCount);

      let badge = cartLink.querySelector('.wi-header__cart-count');
      if (safeCount > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'wi-header__cart-count';
          badge.setAttribute('aria-hidden', 'true');
          cartLink.appendChild(badge);
        }
        badge.textContent = safeCount > 99 ? '99+' : String(safeCount);
      } else {
        badge?.remove();
      }
    });
  };

  const initCart = (root) => {
    if (!root || root.dataset.wiCartReady === 'true') return;
    root.dataset.wiCartReady = 'true';

    const sectionId = root.dataset.wiCartSectionId;
    if (!sectionId) return;

    const states = new Map();
    const liveStatus = root.querySelector('[data-wi-cart-status]');

    const announce = (message) => {
      if (liveStatus && message) liveStatus.textContent = message;
    };

    const getItem = (target) => target.closest('[data-wi-cart-item]');

    const getState = (item) => {
      const key = item?.dataset.wiCartKey;
      if (!key) return null;

      if (!states.has(key)) {
        const input = item.querySelector('[data-wi-cart-quantity]');
        states.set(key, {
          key,
          desired: Math.max(0, Number(input?.value) || 0),
          confirmed: Math.max(0, Number(input?.value) || 0),
          inFlight: false,
          timer: 0,
          item
        });
      }

      return states.get(key);
    };

    const replaceSummary = (freshRoot) => {
      const currentSummary = root.querySelector('[data-wi-cart-summary]');
      const freshSummary = freshRoot.querySelector('[data-wi-cart-summary]');
      if (currentSummary && freshSummary) {
        currentSummary.replaceWith(freshSummary);
      }
    };

    const reconcileCartItems = (freshRoot, activeState, sentQuantity) => {
      const freshItems = Array.from(freshRoot.querySelectorAll('[data-wi-cart-item]'));
      const currentItems = Array.from(root.querySelectorAll('[data-wi-cart-item]'));
      const freshKeyMap = new Map(freshItems.map((item) => [item.dataset.wiCartKey, item]));

      // 1. Remove line items that no longer exist in the fresh response
      currentItems.forEach((item) => {
        const key = item.dataset.wiCartKey;
        if (!freshKeyMap.has(key)) {
          item.classList.add('is-removing');
          window.setTimeout(() => item.remove(), 180);
        }
      });

      // 2. Synchronize pricing, discounts, and controls across all matching items
      freshItems.forEach((freshItem) => {
        const key = freshItem.dataset.wiCartKey;
        const currentItem = root.querySelector(`[data-wi-cart-item][data-wi-cart-key="${CSS.escape(key)}"]`);
        if (!currentItem) return;

        // Reconcile prices & discounts dynamically (handles cross-item tiered & bundle discounts)
        const currentPrice = currentItem.querySelector('[data-wi-cart-price-container]');
        const freshPrice = freshItem.querySelector('[data-wi-cart-price-container]');
        if (currentPrice && freshPrice) currentPrice.replaceWith(freshPrice);

        const currentDiscounts = currentItem.querySelector('[data-wi-cart-discounts]');
        const freshDiscounts = freshItem.querySelector('[data-wi-cart-discounts]');
        if (currentDiscounts && freshDiscounts) currentDiscounts.replaceWith(freshDiscounts);

        // Clear any previous error messaging
        const errorEl = currentItem.querySelector('[data-wi-cart-item-error]');
        if (errorEl) {
          errorEl.hidden = true;
          errorEl.textContent = '';
        }

        // For the item actively being modified: reconcile input without destroying active focus
        if (activeState && key === activeState.key && activeState.desired === sentQuantity) {
          const input = currentItem.querySelector('[data-wi-cart-quantity]');
          if (input && document.activeElement !== input) {
            input.value = String(sentQuantity);
          }
        }
      });
    };

    const sendState = async (state) => {
      if (!state || state.inFlight || state.desired === state.confirmed) return;

      const sentQuantity = state.desired;
      state.inFlight = true;
      state.item.classList.add('is-updating');

      const errorEl = state.item.querySelector('[data-wi-cart-item-error]');
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }

      try {
        const response = await fetch(getChangeUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            id: state.key,
            quantity: sentQuantity,
            sections: [sectionId],
            sections_url: window.location.pathname
          })
        });

        const cart = await response.json();

        if (!response.ok || cart.status) {
          throw new Error(cart.description || cart.message || `Cart update failed (${response.status}).`);
        }

        const sectionHtml = cart.sections?.[sectionId];
        if (!sectionHtml) {
          throw new Error('Cart section markup was not returned.');
        }

        const freshRoot = parseSection(
          sectionHtml,
          `[data-wi-cart-section-id="${CSS.escape(sectionId)}"]`
        );

        if (!freshRoot) {
          throw new Error('Fresh section could not be parsed.');
        }

        state.confirmed = sentQuantity;

        // Dispatches global events for theme integration
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        document.dispatchEvent(new CustomEvent('wi:cart:updated', { detail: { cart, cartCount: cart.item_count } }));

        if (cart.item_count === 0) {
          root.replaceWith(freshRoot);
          initCart(freshRoot);
          updateHeaderCount(0);
          announce('Your cart is now empty.');
          return;
        }

        reconcileCartItems(freshRoot, state, sentQuantity);
        replaceSummary(freshRoot);
        updateHeaderCount(cart.item_count);

        announce(sentQuantity === 0 ? 'Item removed from cart.' : 'Cart updated.');
      } catch (error) {
        console.warn('[WI Cart] Mutation error:', error);

        const input = state.item?.querySelector('[data-wi-cart-quantity]');
        if (input) input.value = String(state.confirmed);
        state.desired = state.confirmed;

        if (errorEl) {
          errorEl.textContent = error.message || 'Unable to update item quantity.';
          errorEl.hidden = false;
        }

        announce(error.message || 'Cart update failed.');
      } finally {
        state.inFlight = false;
        state.item.classList.remove('is-updating');

        if (state.desired !== state.confirmed) {
          sendState(state);
        }
      }
    };

    const scheduleState = (state, immediate = false) => {
      if (!state) return;
      window.clearTimeout(state.timer);

      if (immediate) {
        sendState(state);
        return;
      }

      state.timer = window.setTimeout(() => {
        state.timer = 0;
        sendState(state);
      }, DEBOUNCE_MS);
    };

    const setDesiredQuantity = (item, quantity, immediate = false) => {
      const state = getState(item);
      if (!state) return;

      const input = item.querySelector('[data-wi-cart-quantity]');
      const min = Number(input?.min || 0);
      const max = input?.max ? Number(input.max) : Infinity;

      let safeQty = Math.max(0, Number(quantity) || 0);
      if (safeQty > 0) {
        safeQty = Math.min(Math.max(safeQty, min), max);
      }

      state.desired = safeQty;
      if (input && document.activeElement !== input) {
        input.value = String(state.desired);
      }

      scheduleState(state, immediate);
    };

    // Keyboard & Form Safeguard: Neutralizes Enter key submitting form to /cart
    root.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target.matches('[data-wi-cart-quantity]')) {
        event.preventDefault();
        const input = event.target;
        const item = getItem(input);
        setDesiredQuantity(item, input.value, true);
        input.blur();
      }
    });

    root.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-wi-cart-remove]');
      if (remove) {
        event.preventDefault();
        const item = getItem(remove);
        setDesiredQuantity(item, 0, true);
        return;
      }

      const minus = event.target.closest('[data-wi-cart-quantity-minus]');
      const plus = event.target.closest('[data-wi-cart-quantity-plus]');
      if (!minus && !plus) return;

      const item = getItem(event.target);
      const state = getState(item);
      if (!state) return;

      const step = Number(item.querySelector('[data-wi-cart-quantity]')?.step) || 1;
      setDesiredQuantity(item, minus ? state.desired - step : state.desired + step);
    });

    root.addEventListener('change', (event) => {
      const input = event.target.closest('[data-wi-cart-quantity]');
      if (!input) return;

      const item = getItem(input);
      setDesiredQuantity(item, input.value, true);
    });
  };

  const initAllCarts = (scope = document) => {
    scope.querySelectorAll('[data-wi-cart]').forEach(initCart);
  };

  initAllCarts();

  document.addEventListener('shopify:section:load', (event) => {
    initAllCarts(event.target);
  });
})();